import React from 'react';
import PropTypes from 'prop-types';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  Divider,
  Grid
} from '@mui/material';

const InfoCard = ({ title, subtitle, icon, items, chips = [], variant = 'outlined' }) => {
  return (
    <Card 
      variant={variant} 
      sx={{ 
        mb: 2, 
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          boxShadow: 3,
          transform: 'translateY(-1px)'
        }
      }}
    >
      <CardContent sx={{ p: 3 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          {icon && (
            <Box sx={{ mr: 2, color: 'primary.main' }}>
              {icon}
            </Box>
          )}
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="h6" component="h3" sx={{ fontWeight: 600 }}>
              {title}
            </Typography>
            {subtitle && (
              <Typography variant="body2" color="text.secondary">
                {subtitle}
              </Typography>
            )}
          </Box>
          {chips.length > 0 && (
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {chips.map((chip, index) => (
                <Chip
                  key={index}
                  label={chip.label}
                  color={chip.color || 'default'}
                  size="small"
                  variant={chip.variant || 'filled'}
                  icon={chip.icon}
                />
              ))}
            </Box>
          )}
        </Box>

        {/* Content */}
        {items.length > 0 && (
          <>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={2}>
              {items.map((item, index) => (
                <Grid item xs={12} sm={6} md={4} key={index}>
                  <Box>
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        display: 'block', 
                        fontWeight: 500, 
                        color: 'text.secondary',
                        textTransform: 'uppercase',
                        letterSpacing: 0.5
                      }}
                    >
                      {item.label}
                    </Typography>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        fontWeight: 500,
                        color: item.highlight ? 'primary.main' : 'text.primary'
                      }}
                    >
                      {item.value || 'N/A'}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </>
        )}
      </CardContent>
    </Card>
  );
};

InfoCard.propTypes = {
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string,
  icon: PropTypes.node,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      highlight: PropTypes.bool,
    })
  ),
  chips: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      color: PropTypes.string,
      variant: PropTypes.string,
      icon: PropTypes.node,
    })
  ),
  variant: PropTypes.oneOf(['outlined', 'elevation']),
};

InfoCard.defaultProps = {
  subtitle: null,
  icon: null,
  items: [],
  chips: [],
  variant: 'outlined',
};

export default InfoCard;
